/**
 * NsfwGateOverlay
 *
 * Full-area tap-to-reveal layer rendered over the video while the explicit
 * content gate is active. Mirrors Flutter's `ExplicitContentWidget`
 * (lib/presentation/screens/content/custom_widgets/common/explicit_content_widget.dart).
 *
 * Only the "Show anyway" button is interactive — taps elsewhere on the
 * overlay are inert (matching Flutter's `GestureDetector`-only reveal).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ExpauseButton, ButtonVariant, ButtonSize } from '@/presentation/components/common/controls/ExpauseButton';

export interface NsfwGateOverlayProps {
  /** Reveal callback — fires when the user taps the "Show anyway" button. */
  onReveal: () => void;
}

export const NsfwGateOverlay: React.FC<NsfwGateOverlayProps> = ({ onReveal }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center"
      style={{
        pointerEvents: 'auto',
        backgroundColor: theme.modalOverlay,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        padding: responsive.spacing.lg,
        gap: responsive.spacing.base,
      }}
    >
      <div
        style={{
          color: theme.text.primary,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.bold,
        }}
      >
        {l10n.content.nsfwInfoTitle}
      </div>

      <div
        style={{
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
          fontWeight: responsive.fontWeight.regular,
          textAlign: 'center',
          maxWidth: '40%',
          opacity: 0.85,
        }}
      >
        {l10n.content.nsfwInfoMessage}
      </div>

      {/* Spacer */}
      <div style={{ height: responsive.spacing.lg }} />

      <ExpauseButton
        variant={ButtonVariant.secondary}
        size={ButtonSize.medium}
        onClick={onReveal}
      >
        {l10n.content.nsfwTapToReveal}
      </ExpauseButton>
    </div>
  );
};
